/**
 * Numeric min/max range filter for the SOH Details settings sidebar.
 *
 * Same accordion shell and temp-then-Apply behaviour as SOHCategoryFilter, but with the
 * min/max input pair reused from the generic table's RangeFilter so a range filter looks
 * and validates identically wherever it appears.
 *
 * Emits { start, end } as strings. Either bound may be empty — the backend treats an
 * empty bound as open-ended, so "50000 and above" is a valid filter. Clearing both and
 * pressing Apply removes the filter.
 *
 * Accepts non-negative numbers only, which suits every column it currently filters
 * (odometer). A column that can legitimately go below zero needs an opt-in prop here
 * rather than a loosened pattern — see isCompleteBound.
 */
import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import PrimaryChip from "../../../../../Components/PrimaryChip";
import { PrimaryFormActionButton } from "../../../../../Components/StyledComponent";
import { RangeTextField } from "../../../../../Features/Table/GenericDraggableTable/TableFilters/filterTypes/RangeFilter";

const EMPTY_RANGE = { start: "", end: "" };

// Permissive while typing so intermediate states like "12." are not rejected mid-entry:
// digits, at most one decimal point. Whether the result is a usable bound is decided by
// isCompleteBound, not by this.
const NUMERIC_INPUT_PATTERN = /^\d*\.?\d*$/;

/**
 * Whether a bound is finished and usable. NUMERIC_INPUT_PATTERN also admits "" and "."
 * which are not numbers, and the backend drops the whole range entry when it cannot
 * parse a bound — so allowing Apply on one of those would show an applied-filter chip
 * over completely unfiltered data.
 *
 * Negative values are rejected too: an odometer reading cannot be below zero.
 */
export const isCompleteBound = (value) =>
  value !== "" && Number.isFinite(Number(value)) && Number(value) >= 0;

const formatBound = (value, unit) => {
  const formatted = isCompleteBound(value) ? Number(value).toLocaleString() : value;
  return unit ? `${formatted} ${unit}` : formatted;
};

// "1,000 km - 50,000 km" / ">= 1,000 km" / "<= 50,000 km"
export const getRangeChipLabel = (range = EMPTY_RANGE, unit = "") => {
  const start = range?.start ?? "";
  const end = range?.end ?? "";
  if (start === "" && end === "") return "";
  if (start !== "" && end !== "") {
    return `${formatBound(start, unit)} - ${formatBound(end, unit)}`;
  }
  return start !== ""
    ? `>= ${formatBound(start, unit)}`
    : `<= ${formatBound(end, unit)}`;
};

// Both bounds are optional, but each one supplied must be a usable number, and a closed
// range must not be inverted.
export const getRangeValidationError = (range = EMPTY_RANGE) => {
  const { start = "", end = "" } = range || {};
  if ((start !== "" && !isCompleteBound(start)) || (end !== "" && !isCompleteBound(end))) {
    return "Enter a valid number";
  }
  if (start === "" || end === "") return "";
  if (Number(end) <= Number(start)) {
    return "Maximum value must be greater than minimum value";
  }
  return "";
};

/**
 * Whether pressing Apply would actually change anything. Guards against a redundant
 * onChange: the parent rebuilds its filter payload objects on every patch, so a no-op
 * Apply would refire both the table and the chart fetch.
 */
export const hasRangeChanged = (next = EMPTY_RANGE, applied = EMPTY_RANGE) =>
  (next?.start ?? "") !== (applied?.start ?? "") ||
  (next?.end ?? "") !== (applied?.end ?? "");

// ─── Component ────────────────────────────────────────────────────────────────
const NumericRangeFilter = ({
  title,
  unit = "",
  selectedRange = EMPTY_RANGE,
  onChange,
  testIdPrefix = "numeric-range",
}) => {
  const [tempRange, setTempRange] = useState(selectedRange || EMPTY_RANGE);
  const [appliedRange, setAppliedRange] = useState(selectedRange || EMPTY_RANGE);

  // Sync with parent-controlled state (e.g. when parent resets filters). Keyed on the
  // two bounds rather than the object, so a caller passing an inline default such as
  // `selectedRange={filters.odometer || { start: "", end: "" }}` does not re-sync (and
  // re-render) on every parent render.
  //
  // Consequence: a parent "reset all" that leaves both bounds unchanged (because nothing
  // was applied) does not clear a value the user typed but never applied. Callers cover
  // that by remounting on reset — see the `key={`odometer-${resetKey}`}` in
  // SOHDetailsSettingsSidebar. Keep that key if you add another caller.
  const selectedStart = selectedRange?.start ?? "";
  const selectedEnd = selectedRange?.end ?? "";
  useEffect(() => {
    setTempRange({ start: selectedStart, end: selectedEnd });
    setAppliedRange({ start: selectedStart, end: selectedEnd });
  }, [selectedStart, selectedEnd]);

  const error = getRangeValidationError(tempRange);
  const hasTempValue = tempRange.start !== "" || tempRange.end !== "";
  const appliedLabel = getRangeChipLabel(appliedRange, unit);

  const handleBoundChange = (bound) => (event) => {
    const value = event.target.value;
    if (value === "" || NUMERIC_INPUT_PATTERN.test(value)) {
      setTempRange((prev) => ({ ...prev, [bound]: value }));
    }
  };

  const handleClearSelection = () => setTempRange(EMPTY_RANGE);

  const handleApply = () => {
    if (error || !hasRangeChanged(tempRange, appliedRange)) return;
    setAppliedRange(tempRange);
    // Propagate the cleared range too, so emptying both boxes removes the filter.
    if (onChange) onChange(tempRange);
  };

  const handleChipDelete = () => {
    setAppliedRange(EMPTY_RANGE);
    setTempRange(EMPTY_RANGE);
    if (onChange) onChange(EMPTY_RANGE);
  };

  return (
    <Accordion
      sx={{
        boxShadow: "none",
        borderBottom: "1px solid #E6E6E6",
        my: 0,
        "&:before": { display: "none" },
        "&.Mui-expanded": { my: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        data-testid={`${testIdPrefix}-filter-summary`}
        sx={{
          paddingTop: "18px",
          paddingBottom: "18px",
          alignItems: "flex-start",
          "&.Mui-expanded": { minHeight: "auto" },
          "& .MuiAccordionSummary-content": {
            margin: 0,
            alignItems: "flex-start",
            gap: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            variant="fw400s14c000000"
            sx={{ lineHeight: "16px", color: "#1C1C1C", mr: 1 }}
          >
            {title}
          </Typography>
          {appliedLabel && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
              onClick={(e) => e.stopPropagation()}
            >
              <PrimaryChip
                label={appliedLabel}
                tooltipTitle={appliedLabel}
                tooltipPlacement="bottom"
                onDelete={handleChipDelete}
                deleteIcon={<CloseIcon data-testid={`${testIdPrefix}-chip-delete`} />}
                data-testid={`${testIdPrefix}-chip`}
              />
            </Box>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <RangeTextField
              label="Minimum"
              placeholder="Min"
              value={tempRange.start}
              onChange={handleBoundChange("start")}
              error={error}
              unit={unit}
              dataTestId={`${testIdPrefix}-min-input`}
            />
            <RangeTextField
              label="Maximum"
              placeholder="Max"
              value={tempRange.end}
              onChange={handleBoundChange("end")}
              error={error}
              unit={unit}
              dataTestId={`${testIdPrefix}-max-input`}
            />
          </Box>

          {error && (
            <Typography variant="fw400s10cB50500" sx={{ lineHeight: "20px" }}>
              {error}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Typography
              variant="fw500s12lh24c6D72F6"
              sx={{
                cursor: hasTempValue ? "pointer" : "not-allowed",
                color: hasTempValue ? "#6D72F6" : "#8F8F8F",
              }}
              onClick={() => hasTempValue && handleClearSelection()}
              data-testid={`${testIdPrefix}-clear-btn`}
            >
              Clear Selection
            </Typography>
            <PrimaryFormActionButton
              onClick={handleApply}
              disabled={!!error}
              sx={{ py: 0.75, px: 2, fontSize: "12px" }}
              data-testid={`${testIdPrefix}-apply-btn`}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default NumericRangeFilter;
